import { useState } from "react";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { BrutalTextarea } from "@/components/ui/BrutalTextarea";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { RichTextEditor } from "@/components/editor/RichTextEditor";
import { Upload, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { sanitizeName, sanitizeText, sanitizeRichText } from "@/lib/sanitize";

interface BlogSubmissionFormProps {
  onBack: () => void;
  onSubmit: (data: {
    title: string;
    metaDescription: string;
    contentHtml: string;
    contentPlainText: string;
    wordCount: number;
    featuredImage: File | null;
    tags: string[];
    notes: string;
  }) => Promise<void>;
  isSubmitting: boolean;
}

export const BlogSubmissionForm = ({ onBack, onSubmit, isSubmitting }: BlogSubmissionFormProps) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [contentHtml, setContentHtml] = useState("");
  const [contentPlainText, setContentPlainText] = useState("");
  const [wordCount, setWordCount] = useState(0);
  const [featuredImage, setFeaturedImage] = useState<File | null>(null);
  const [tagsInput, setTagsInput] = useState("");
  const [notes, setNotes] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (title.length < 20) {
      newErrors.title = t("creator.validation.titleTooShort");
    }

    if (title.length > 100) {
      newErrors.title = t("creator.validation.titleTooLong");
    }

    if (metaDescription.length < 50) {
      newErrors.metaDescription = t("creator.validation.metaTooShort");
    }

    if (metaDescription.length > 160) {
      newErrors.metaDescription = t("creator.validation.metaTooLong");
    }

    if (wordCount < 500) {
      newErrors.content = t("creator.validation.notEnoughWords");
    }

    if (!featuredImage) {
      newErrors.featuredImage = t("creator.validation.featuredImageRequired");
    }

    const tags = tagsInput.split(',').map(t => t.trim()).filter(t => t);
    if (tags.length < 2) {
      newErrors.tags = t("creator.validation.tagsRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const tags = tagsInput.split(',').map(t => t.trim()).filter(t => t);

    await onSubmit({
      title: sanitizeName(title),
      metaDescription: sanitizeText(metaDescription),
      contentHtml: sanitizeRichText(contentHtml),
      contentPlainText: sanitizeText(contentPlainText),
      wordCount,
      featuredImage,
      tags: tags.map(t => sanitizeText(t)),
      notes: sanitizeText(notes),
    });
  };

  return (
    <BrutalCard className="mb-8">
      <div className="flex items-center gap-4 mb-6">
        <BrutalButton
          type="button"
          variant="outline"
          size="sm"
          onClick={onBack}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t("creator.backToSelection")}
        </BrutalButton>
        <h2 className="font-heading text-2xl">{t("creator.blogForm.title")}</h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block font-heading text-sm mb-2">
            {t("creator.blogForm.articleTitle")} *
          </label>
          <BrutalInput
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("creator.blogForm.articleTitlePlaceholder")}
            maxLength={100}
          />
          <p className="text-xs text-muted-foreground mt-1">
            {title.length}/100 caratteri
          </p>
          {errors.title && (
            <p className="text-warning text-sm mt-1">{errors.title}</p>
          )}
        </div>

        <div>
          <label className="block font-heading text-sm mb-2">
            {t("creator.blogForm.metaDescription")} *
          </label>
          <BrutalTextarea
            value={metaDescription}
            onChange={(e) => setMetaDescription(e.target.value)}
            placeholder={t("creator.blogForm.metaDescPlaceholder")}
            maxLength={160}
            rows={2}
          />
          <p className="text-xs text-muted-foreground mt-1">
            {metaDescription.length}/160 caratteri
          </p>
          {errors.metaDescription && (
            <p className="text-warning text-sm mt-1">{errors.metaDescription}</p>
          )}
        </div>

        <div>
          <label className="block font-heading text-sm mb-2">
            {t("creator.blogForm.featuredImage")} *
          </label>
          <div className="border-[3px] border-dark p-4">
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) setFeaturedImage(file);
              }}
              className="hidden"
              id="featured-image-upload"
            />
            <label
              htmlFor="featured-image-upload"
              className="flex flex-col items-center gap-2 p-6 border-[3px] border-dashed border-dark cursor-pointer hover:bg-accent/10 transition-colors"
            >
              {featuredImage ? (
                <div className="relative">
                  <img
                    src={URL.createObjectURL(featuredImage)}
                    alt="Featured image preview"
                    className="max-h-48 border-2 border-dark"
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setFeaturedImage(null);
                    }}
                    className="absolute -top-2 -right-2 bg-destructive text-white p-1 rounded-full border-2 border-dark"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <>
                  <Upload className="w-8 h-8 text-muted-foreground" />
                  <p className="text-sm font-medium">Carica immagine di copertina</p>
                  <p className="text-xs text-muted-foreground">JPG, PNG o WEBP • Max 2MB</p>
                </>
              )}
            </label>
          </div>
          {errors.featuredImage && (
            <p className="text-warning text-sm mt-1">{errors.featuredImage}</p>
          )}
        </div>

        <div>
          <label className="block font-heading text-sm mb-2">
            {t("creator.blogForm.content")} *
          </label>
          <RichTextEditor
            value={contentHtml}
            onChange={(html, plainText, words) => {
              setContentHtml(html);
              setContentPlainText(plainText);
              setWordCount(words);
            }}
            placeholder={t("creator.blogForm.contentPlaceholder")}
            minWords={500}
          />
          {errors.content && (
            <p className="text-warning text-sm mt-1">{errors.content}</p>
          )}
        </div>

        <div>
          <label className="block font-heading text-sm mb-2">
            {t("creator.blogForm.tags")} *
          </label>
          <BrutalInput
            value={tagsInput}
            onChange={(e) => setTagsInput(e.target.value)}
            placeholder={t("creator.blogForm.tagsPlaceholder")}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Separa i tag con virgole. Min 2 tag richiesti.
          </p>
          {errors.tags && (
            <p className="text-warning text-sm mt-1">{errors.tags}</p>
          )}
        </div>

        <div>
          <label className="block font-heading text-sm mb-2">
            {t("creator.blogForm.notes")}
          </label>
          <BrutalTextarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Note per amministratore..."
            rows={3}
          />
        </div>

        <div className="border-t-[3px] border-dark pt-6">
          <BrutalButton
            type="submit"
            size="lg"
            disabled={isSubmitting}
            className="w-full"
          >
            {isSubmitting ? "Invio in corso..." : t("creator.blogForm.submit")}
          </BrutalButton>
        </div>
      </form>
    </BrutalCard>
  );
};
